import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { toastify } from "@helpers/toast";
import { PUBLISHER_ENDPOINTS } from "@constants/endpoints";
import { countAwaiting, normalizeContracts } from "../misc/helpers";

// The services wrap their payload in `data` at one or two levels depending on
// the gateway in front of them, so unwrap defensively rather than indexing.
const payloadOf = (res) => {
    const body = res?.data ?? res;
    return body?.data ?? body;
};

// Owns every server interaction for the publisher Contracts page: the contract
// ops sent to this publisher account, and the publisher's signature on it.
const usePublisherContracts = (publisherId) => {
    // showAlert is off so a publisher ops has not sent a contract to yet
    // renders the empty state instead of an error toast.
    const { axiosInstance, loading: fetching } = useAxios({ base: "affiliate", initialLoader: true, showAlert: false });
    const { axiosInstance: ackInstance, loading: accepting } = useAxios({ base: "affiliate", showAlert: false });

    const [contracts, setContracts] = useState([]);

    const fetchContracts = async () => {
        if (!publisherId) return;

        try {
            const data = payloadOf(await axiosInstance.get(PUBLISHER_ENDPOINTS.CONTRACT(publisherId)));
            setContracts(normalizeContracts(data));
        } catch {
            setContracts([]);
        }
    };

    useEffect(() => {
        fetchContracts();
    }, [publisherId]); // eslint-disable-line react-hooks/exhaustive-deps

    // The endpoint takes no body: the contract being acknowledged is whichever
    // one the server is currently serving, and it stamps the record itself.
    //
    // That is deliberate on the server's part — a timestamp, IP or version the
    // client can set is evidence the acknowledging party can dispute. The
    // client's job is only to make sure the document was genuinely put in
    // front of the publisher before this call is reachable, which is what the
    // review modal's gating does.
    //
    // The response echoes the new state, but the row is re-read instead of
    // patched so the file details and the status can never disagree.
    const acknowledgeContract = async () => {
        if (!publisherId) return false;

        try {
            await ackInstance.post(PUBLISHER_ENDPOINTS.CONTRACT_SIGN(publisherId));
            await fetchContracts();
            toastify({ title: "Contract signed.", type: "success" });
            return true;
        } catch (error) {
            toastify({
                title: error?.response?.data?.message || "Could not sign the contract. Please try again.",
                type : "error"
            });
            return false;
        }
    };

    return {
        loading      : fetching,
        accepting,
        contracts,
        awaitingCount: countAwaiting(contracts),
        acknowledgeContract,
        refetch      : fetchContracts
    };
};

export default usePublisherContracts;
